import { useCallback, useId, useRef, useState } from "react";
import type { DeckorateCommand, Tab } from "../../../types";
import { runDomAction } from "../services/runAction";
import { requestChatSheet } from "../../action-chips/services/chatSheetBridge";
import { useActionSearch, usePaletteChrome, useEscapeToClose, handleSharedCommandKinds } from "../hooks/useCommandPalette";
import { ActionResultsList, ActionCatalogFooter, AiPreviewPanel, DialogTopbar, SearchInputRow } from "./PaletteViews";
import { logError, scrubMessage } from "../../../../../telemetry";
import styles from "./CommandPalette.module.css";

/** Tags telemetry events so they slice under the "integration" backend, matching
 * colourOps.ts's/mapPlotter.ts's convention. */
const CMD_PALETTE_CONNECTOR = "deckorate";

/** Reports a CommandPalette failure through the shared (PII-scrubbed) telemetry pipeline. */
function logCmdPaletteError(reason: string, type: string, e: unknown): void {
  logError(
    "error.connector.failure",
    { type, scrubbedMessage: scrubMessage(e instanceof Error ? e.message : String(e)) },
    { connectorSystemName: CMD_PALETTE_CONNECTOR, reason },
  );
}

/** Deckorate runs its own commands (currentTab/onNavigate); the pane's merged
 * "All actions" palette (ST-05) passes its own list and runner instead. */
type CommandRunner =
  | { readonly currentTab: Tab; readonly onNavigate: (tab: Tab) => void; readonly onRunCommand?: never }
  | { readonly onRunCommand: (cmd: DeckorateCommand) => void; readonly currentTab?: never; readonly onNavigate?: never };

type CommandPaletteProps = CommandRunner & {
  readonly onClose: () => void;
  /** Defaults to Deckorate's ACTION_CATALOG. */
  readonly catalog?: readonly DeckorateCommand[];
  /** Footer direct-key codes are Deckorate's, so off when its actions aren't listed. */
  readonly showCodes?: boolean;
};

export function CommandPalette({
  currentTab,
  onNavigate,
  onRunCommand,
  onClose,
  catalog,
  showCodes = true,
}: Readonly<CommandPaletteProps>) {
  const listboxId = useId();
  const { query, setQuery, activeIndex, setActiveIndex, results, move, toggleFavorite, recordRecent } =
    useActionSearch(catalog);
  // Set while an "ai-preview" command's dialog replaces the search list.
  const [aiCommand, setAiCommand] = useState<DeckorateCommand | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const { notice, showNotice } = usePaletteChrome(panelRef, inputRef, onClose);
  // Guards against a rapid double mousedown re-running a non-idempotent
  // dom-click (e.g. "Duplicate Right") before the palette closes.
  const runningRef = useRef(false);

  const runCommand = useCallback(
    async (cmd: DeckorateCommand | undefined) => {
      if (!cmd || runningRef.current) return;
      runningRef.current = true;
      try {
        recordRecent(cmd.id);

        if (handleSharedCommandKinds(cmd, showNotice, setAiCommand)) return;

        if (onRunCommand) {
          onClose();
          onRunCommand(cmd);
          return;
        }

        // requiresSelection is deliberately NOT checked here — navigate/click
        // through unconditionally and let the target operation throw its own
        // "Select N shapes" error, surfaced by that panel's own error UI,
        // exactly as a manual click there would.
        //
        // onClose() unmounts this component, so it's only called once an
        // async step has settled AND succeeded — otherwise it would hide the
        // very notice that step's failure just raised.
        // Only the Deckorate runner is left here (onRunCommand returned above).
        const navigateTo = onNavigate as (tab: Tab) => void;
        if (cmd.kind === "navigate" && cmd.tab) {
          onClose();
          navigateTo(cmd.tab);
          return;
        }

        // An in-pane sheet lives on the CHAT tab, which this module can't
        // switch to — the bridge's two subscribers (TaskPane, ChatTabPanel) do
        // that between them.
        if (cmd.kind === "open-sheet" && cmd.sheet) {
          onClose();
          requestChatSheet(cmd.sheet);
          return;
        }

        try {
          const succeeded = await runDomAction(cmd, currentTab as Tab, navigateTo, showNotice);
          if (succeeded) onClose();
        } catch (e) {
          logCmdPaletteError("cmd-palette-run-command-failed", "RunCommandError", e);
          showNotice(`Something went wrong running "${cmd.label}" — try again.`);
        }
      } finally {
        runningRef.current = false;
      }
    },
    [onClose, onNavigate, onRunCommand, currentTab, showNotice, recordRecent],
  );

  useEscapeToClose(!!aiCommand, onClose);

  if (aiCommand) {
    return (
      <AiPreviewPanel
        command={aiCommand}
        styles={styles}
        panelRef={panelRef}
        panelTestId="command-palette-panel"
        closeTestId="cmd-palette-close"
        closeAriaLabel="Close command palette"
        onBack={() => setAiCommand(null)}
        onClose={onClose}
      />
    );
  }

  return (
    <dialog
      open
      className={styles.overlay}
      aria-modal="true"
      aria-label={catalog ? "All actions" : "Search Deckorate actions"}
    >
      <div
        ref={panelRef}
        className={styles.panel}
        data-testid="command-palette-panel"
      >
        <DialogTopbar
          styles={styles}
          onClose={onClose}
          closeTestId="cmd-palette-close"
          closeAriaLabel="Close command palette"
        />

        <SearchInputRow
          inputRef={inputRef}
          styles={styles}
          listboxId={listboxId}
          value={query}
          activeIndex={activeIndex}
          resultsCount={results.length}
          testId="cmd-palette-input"
          showIcon
          onChange={setQuery}
          onClose={onClose}
          onMove={move}
          onRunActive={() => runCommand(results[activeIndex])}
        />

        {notice && <output className={styles.notice}>{notice}</output>}

        <ActionResultsList
          listboxId={listboxId}
          results={results}
          activeIndex={activeIndex}
          styles={styles}
          testIdPrefix="cmd-palette"
          showPendingBadge
          onHover={setActiveIndex}
          onSelect={runCommand}
          onToggleFavorite={toggleFavorite}
        />

        <ActionCatalogFooter styles={styles} count={catalog?.length} showCodes={showCodes} showKeyboardHints />
      </div>
    </dialog>
  );
}
